import { createFileRoute, Link } from "@tanstack/react-router";
import { useState } from "react";
import {
  ShieldCheck,
  Snowflake,
  Fish,
  Waves,
  Plane,
  Ship,
  Clock,
  ArrowRight,
  CheckCircle2,
  ClipboardList,
  ChevronRight,
  Package,
  Layers,
  ThermometerSnowflake,
} from "lucide-react";
import { Section } from "@/components/layout/Page";
import { Button } from "@/components/ui/button";
import { QuoteDrawer } from "@/components/layout/QuoteDrawer";
import { getProduct } from "@/lib/mock/products";
import { useI18n } from "@/context/I18nContext";
import { getLocalizedMeta } from "@/lib/utils/seo";
import { SUBMISSION_SOURCES } from "@/lib/submission-context";
import heroFlounder from "@/assets/optimized/olive-flounder-1024.webp";
import liveBasketImg from "@/assets/products/optimized/olive-flounder-live-basket-1024.webp";
import filletSpecsImg from "@/assets/products/optimized/olive-flounder-fillet-specs-1024.webp";
import filletVacuumImg from "@/assets/products/optimized/olive-flounder-fillet-vacuum-pack-1024.webp";
import vacuumEpsImg from "@/assets/products/optimized/olive-flounder-vacuum-pack-eps-1024.webp";
import { FaqSection } from "@/components/layout/FaqSection";
import { faqHeadScript } from "@/lib/utils/faq-schema";
import { breadcrumbSchema, jsonLdScript } from "@/lib/utils/schema-lists";
import { activeDict } from "@/lib/i18n-dict";

export const Route = createFileRoute("/olive-flounder-export")({
  head: () => {
    const dict = activeDict();
    return {
      meta: getLocalizedMeta("oliveFlounderExport"),
      scripts: [
        faqHeadScript("oliveFlounder", "/olive-flounder-export"),
        jsonLdScript(
          "ld-breadcrumb",
          breadcrumbSchema([
            { name: dict["common.nav.home"] ?? "Home", path: "/" },
            { name: dict["cat.seafood"] ?? "Seafood", path: "/products/seafood" },
            {
              name: dict["product.olive-flounder.title"] ?? "Olive Flounder",
              path: "/products/seafood/olive-flounder",
            },
            { name: dict["nav.oliveFlounder"] ?? "Olive Flounder Export" },
          ]),
        ),
      ],
    };
  },
  component: OliveFlounderExportPage,
});

/** Icon per commercial format, keyed by the `key` field in products.ts. */
const FORMAT_ICONS: Record<string, typeof Snowflake> = {
  "frozen-fillet": Snowflake,
  "fresh-gutted": Fish,
  live: Waves,
};

function OliveFlounderExportPage() {
  const { t } = useI18n();
  const [quoteOpen, setQuoteOpen] = useState(false);

  // Formats and documents come from the catalogue entry rather than being
  // restated here. They already drive the product page's Export formats tab,
  // and duplicating them would let the two drift the first time a size band
  // or a certificate changed.
  const product = getProduct("seafood", "olive-flounder");
  const formats = product?.exportForms ?? [];
  const documents = product?.exportDocuments ?? [];

  /** Product-scoped translation, matching ProductCard/product detail's tp(). */
  const tp = (field: string, fallback: string): string => {
    const key = `product.olive-flounder.${field}`;
    const res = t(key);
    return res === key ? fallback : res;
  };

  return (
    <>
      {/* Hero */}
      <section className="relative isolate overflow-hidden bg-navy text-white">
        <img
          src={heroFlounder}
          alt={t("flounderExport.hero.imageAlt")}
          className="absolute inset-0 h-full w-full object-cover"
          loading="eager"
          fetchPriority="high"
        />
        <div className="absolute inset-0 bg-gradient-to-r from-navy via-navy/95 to-navy/70" />
        <div className="absolute inset-0 bg-gradient-to-t from-navy via-transparent to-navy/60" />

        <div className="relative mx-auto max-w-7xl px-4 py-16 md:px-6 md:py-24 lg:py-28">
          <div className="grid gap-12 lg:grid-cols-12 lg:items-center">
            <div className="lg:col-span-7">
              {/* Breadcrumb indicator */}
              <nav
                aria-label="Breadcrumbs"
                className="mb-6 flex items-center gap-2 text-xs font-semibold text-white/60"
              >
                <Link to="/" className="transition-colors hover:text-white">
                  {t("common.nav.home")}
                </Link>
                <ChevronRight className="h-3.5 w-3.5" />
                <Link
                  to="/products/$category"
                  params={{ category: "seafood" }}
                  className="transition-colors hover:text-white"
                >
                  {t("cat.seafood")}
                </Link>
                <ChevronRight className="h-3.5 w-3.5" />
                <span className="text-mint font-bold">{t("nav.oliveFlounder")}</span>
              </nav>

              <div className="mb-4 inline-flex items-center gap-3 text-xs font-semibold uppercase tracking-[0.18em] text-mint">
                <span className="inline-block h-[2px] w-8 bg-mint" />
                {t("flounderExport.hero.eyebrow")}
              </div>

              <h1 className="font-display text-3xl sm:text-4xl font-bold leading-tight text-white md:text-5xl lg:text-6xl">
                {t("flounderExport.hero.title")}
              </h1>
              {/* Scientific name adjacent to the trade name, per the species
                  ruling in docs/project/localization/agents-and-tone.md §3b. */}
              <p className="mt-3 text-sm font-medium italic text-mint/90">Paralichthys olivaceus</p>

              <p className="mt-5 max-w-2xl text-base leading-relaxed text-white/75 md:text-lg">
                {t("flounderExport.hero.description")}
              </p>

              <div className="mt-8 flex flex-wrap items-center gap-3">
                <Button
                  size="lg"
                  onClick={() => setQuoteOpen(true)}
                  className="rounded-xl bg-primary font-bold text-primary-foreground shadow-lg shadow-primary/20"
                >
                  {t("flounderExport.hero.cta")} <ArrowRight className="ms-2 h-4 w-4" />
                </Button>
                <Link
                  to="/products/$category/$slug"
                  params={{ category: "seafood", slug: "olive-flounder" }}
                  className="inline-flex items-center gap-2 rounded-xl border border-white/20 bg-white/10 px-4 py-2.5 text-xs font-bold text-white backdrop-blur-sm transition-colors hover:border-mint/60 hover:bg-white/20"
                >
                  <Fish className="h-4 w-4 text-mint" />
                  {tp("title", "Olive Flounder")} {t("glossary.viewProduct")}
                </Link>
                {/* Format chips double as in-page navigation. */}
                {formats.map((f, i) => (
                  <a
                    key={f.key}
                    href={`#format-${f.key}`}
                    className="inline-flex items-center gap-2 rounded-xl border border-white/10 bg-white/5 px-3.5 py-2 text-xs font-semibold text-white/80 backdrop-blur-sm transition-colors hover:border-mint/50 hover:bg-white/15"
                  >
                    {tp(`exportForm.${i}.name`, f.name)}
                  </a>
                ))}
              </div>
            </div>

            {/* Visual Hero Showcase Card */}
            <div className="lg:col-span-5">
              <div className="relative mx-auto max-w-md overflow-hidden rounded-2xl border border-white/15 bg-white/5 p-4 shadow-2xl backdrop-blur-md">
                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-3">
                    <div className="group relative aspect-[4/3] overflow-hidden rounded-xl border border-white/10 bg-navy/60">
                      <img
                        src={liveBasketImg}
                        alt="Olive flounder live harvest aeration basket"
                        className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105"
                        loading="lazy"
                      />
                      <div className="absolute inset-0 bg-gradient-to-t from-navy/80 via-transparent to-transparent" />
                      <span className="absolute bottom-2 left-2 text-[11px] font-bold text-white">
                        Live Harvest Basket
                      </span>
                    </div>
                    <div className="group relative aspect-[4/3] overflow-hidden rounded-xl border border-white/10 bg-navy/60">
                      <img
                        src={filletSpecsImg}
                        alt="Olive flounder fillet specifications with fin vs fin removed"
                        className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105"
                        loading="lazy"
                      />
                      <div className="absolute inset-0 bg-gradient-to-t from-navy/80 via-transparent to-transparent" />
                      <span className="absolute bottom-2 left-2 text-[11px] font-bold text-white">
                        Sashimi Fillet Specs
                      </span>
                    </div>
                  </div>
                  <div className="space-y-3">
                    <div className="group relative aspect-[4/3] overflow-hidden rounded-xl border border-white/10 bg-navy/60">
                      <img
                        src={filletVacuumImg}
                        alt="Vacuum-sealed olive flounder sashimi fillet pack"
                        className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105"
                        loading="lazy"
                      />
                      <div className="absolute inset-0 bg-gradient-to-t from-navy/80 via-transparent to-transparent" />
                      <span className="absolute bottom-2 left-2 text-[11px] font-bold text-white">
                        Vacuum Fillet Pack
                      </span>
                    </div>
                    <div className="group relative aspect-[4/3] overflow-hidden rounded-xl border border-white/10 bg-navy/60">
                      <img
                        src={vacuumEpsImg}
                        alt="Export vacuum packaging in EPS insulated cold shipper"
                        className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105"
                        loading="lazy"
                      />
                      <div className="absolute inset-0 bg-gradient-to-t from-navy/80 via-transparent to-transparent" />
                      <span className="absolute bottom-2 left-2 text-[11px] font-bold text-white">
                        EPS Export Shipper
                      </span>
                    </div>
                  </div>
                </div>

                <div className="mt-4 flex items-center justify-between border-t border-white/10 pt-3 text-xs text-white/80">
                  <span className="flex items-center gap-1.5 font-semibold text-mint">
                    <ShieldCheck className="h-4 w-4" /> ASC & GlobalG.A.P Farms
                  </span>
                  <span className="text-[11px] text-white/60">South Korea Origin</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* The four facts a buyer checks before reading anything else. */}
      <Section className="bg-navy py-10 text-white">
        <div className="grid grid-cols-2 gap-6 text-center lg:grid-cols-4">
          {[
            { v: "2.0 – 2.5 kg", k: "flounderExport.stat.maxSize" },
            { v: "2", k: "flounderExport.stat.filletsPerFish" },
            { v: "24 – 30 h", k: "flounderExport.stat.liveWindow" },
            { v: "52", k: "flounderExport.stat.weeks" },
          ].map((s, i) => (
            <div key={s.k} className={i < 3 ? "border-e border-white/10 p-4" : "p-4"}>
              <div className="font-display text-3xl font-bold text-mint">{s.v}</div>
              <div className="mt-1 text-xs font-semibold uppercase tracking-wider text-white/80">
                {t(s.k)}
              </div>
            </div>
          ))}
        </div>
      </Section>

      {/* Commercial formats — the core of the page. */}
      <Section>
        <div className="mx-auto mb-12 max-w-3xl space-y-3 text-center">
          <h2 className="font-display text-3xl font-bold text-navy">
            {t("flounderExport.formats.heading")}
          </h2>
          <p className="text-sm leading-relaxed text-muted-foreground">
            {t("flounderExport.formats.description")}
          </p>
        </div>

        <div className="mx-auto max-w-5xl space-y-8">
          {formats.map((format, fi) => {
            const Icon = FORMAT_ICONS[format.key] ?? Fish;
            const isFrozenFillet = format.key === "frozen-fillet";
            const isLive = format.key === "live";
            const isFreshGutted = format.key === "fresh-gutted";

            return (
              <div
                key={format.key}
                id={`format-${format.key}`}
                className="scroll-mt-24 overflow-hidden rounded-2xl border border-border/80 bg-background shadow-sm transition-all hover:border-mint/50 hover:shadow-md"
              >
                <div className="p-6 md:p-8">
                  <div className="flex flex-wrap items-start justify-between gap-4">
                    <div className="flex items-start gap-3.5">
                      <span className="mt-0.5 flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-mint/15 text-mint-ink">
                        <Icon className="h-6 w-6" aria-hidden="true" />
                      </span>
                      <div>
                        <h3 className="font-display text-2xl font-bold text-navy">
                          {tp(`exportForm.${fi}.name`, format.name)}
                        </h3>
                        <p className="mt-1 max-w-2xl text-sm leading-relaxed text-muted-foreground">
                          {tp(`exportForm.${fi}.summary`, format.summary)}
                        </p>
                      </div>
                    </div>
                    <Button
                      variant="outline"
                      className="rounded-xl border-primary/40 text-xs font-bold hover:bg-primary hover:text-white"
                      onClick={() => setQuoteOpen(true)}
                    >
                      {t("flounderExport.formats.cta")}
                    </Button>
                  </div>

                  {/* Visual Photography Spotlights per format */}
                  {isFrozenFillet && (
                    <div className="mt-6 grid gap-4 rounded-xl border border-border/60 bg-surface-alt/40 p-4 sm:grid-cols-2">
                      <div className="space-y-2">
                        <div className="overflow-hidden rounded-lg border border-border/80 bg-background">
                          <img
                            src={filletSpecsImg}
                            alt="Olive flounder fillet technical cutting specs - with fin versus fin removed"
                            className="h-44 w-full object-cover object-center transition-transform duration-300 hover:scale-105"
                            loading="lazy"
                          />
                        </div>
                        <div className="px-1 text-xs font-semibold text-navy">
                          Fillet Trimming: With Fin vs. Fin Removed
                        </div>
                        <p className="px-1 text-[11px] leading-relaxed text-muted-foreground">
                          Precision sashimi processing: trimmed to exact buyer requirements for
                          sushi and raw-counter slicing.
                        </p>
                      </div>
                      <div className="space-y-2">
                        <div className="overflow-hidden rounded-lg border border-border/80 bg-background">
                          <img
                            src={filletVacuumImg}
                            alt="Vacuum-sealed olive flounder sashimi fillet export packshot"
                            className="h-44 w-full object-cover object-center transition-transform duration-300 hover:scale-105"
                            loading="lazy"
                          />
                        </div>
                        <div className="px-1 text-xs font-semibold text-navy">
                          Individual Barrier Vacuum Pack
                        </div>
                        <p className="px-1 text-[11px] leading-relaxed text-muted-foreground">
                          Hermetically sealed skin packaging locks in moisture, protecting texture
                          and taste across reefer cold storage.
                        </p>
                      </div>
                    </div>
                  )}

                  {isLive && (
                    <div className="mt-6 grid gap-4 rounded-xl border border-border/60 bg-surface-alt/40 p-4 sm:grid-cols-2">
                      <div className="space-y-2">
                        <div className="overflow-hidden rounded-lg border border-border/80 bg-background">
                          <img
                            src={liveBasketImg}
                            alt="Live olive flounder in aeration harvest basket"
                            className="h-44 w-full object-cover object-center transition-transform duration-300 hover:scale-105"
                            loading="lazy"
                          />
                        </div>
                        <div className="px-1 text-xs font-semibold text-navy">
                          Harvest Basket & Holding Conditioning
                        </div>
                        <p className="px-1 text-[11px] leading-relaxed text-muted-foreground">
                          Conditioned in clean ocean-water aeration tanks prior to bagging, ensuring
                          minimal stress and high survival.
                        </p>
                      </div>
                      <div className="flex flex-col justify-center rounded-lg border border-border/80 bg-background p-4">
                        <div className="flex items-center gap-2 text-xs font-bold text-mint-ink">
                          <Clock className="h-4 w-4" /> 24–30 Hour Viability Window
                        </div>
                        <p className="mt-2 text-xs leading-relaxed text-muted-foreground">
                          Live transport relies on strict flight corridors and oxygen-enriched water
                          containers. We calculate door-to-door transit to your airport before
                          confirming orders.
                        </p>
                        <div className="mt-4 flex items-center gap-3 text-xs font-bold text-navy">
                          <span className="rounded-md bg-mint/20 px-2 py-1 text-mint-ink">
                            Live tanks
                          </span>
                          <span className="rounded-md bg-surface-alt px-2 py-1">
                            Direct air freight
                          </span>
                        </div>
                      </div>
                    </div>
                  )}

                  {isFreshGutted && (
                    <div className="mt-6 grid gap-4 rounded-xl border border-border/60 bg-surface-alt/40 p-4 sm:grid-cols-2">
                      <div className="space-y-2">
                        <div className="overflow-hidden rounded-lg border border-border/80 bg-background">
                          <img
                            src={heroFlounder}
                            alt="Fresh whole olive flounder"
                            className="h-44 w-full object-cover object-center transition-transform duration-300 hover:scale-105"
                            loading="lazy"
                          />
                        </div>
                        <div className="px-1 text-xs font-semibold text-navy">
                          Whole Gutted on Flake Ice
                        </div>
                        <p className="px-1 text-[11px] leading-relaxed text-muted-foreground">
                          Harvested and eviscerated under chilled conditions, packed directly onto
                          food-grade flake ice at 0–2 °C.
                        </p>
                      </div>
                      <div className="space-y-2">
                        <div className="overflow-hidden rounded-lg border border-border/80 bg-background">
                          <img
                            src={vacuumEpsImg}
                            alt="EPS thermal cold shipper crate"
                            className="h-44 w-full object-cover object-center transition-transform duration-300 hover:scale-105"
                            loading="lazy"
                          />
                        </div>
                        <div className="px-1 text-xs font-semibold text-navy">
                          EPS Thermal Insulated Shipper
                        </div>
                        <p className="px-1 text-[11px] leading-relaxed text-muted-foreground">
                          High-density expanded polystyrene crates maintain steady chill integrity
                          throughout international air transfer.
                        </p>
                      </div>
                    </div>
                  )}

                  {/* Weight bands are values, not copy — never translated. */}
                  {format.sizes && format.sizes.length > 0 && (
                    <div className="mt-6">
                      <h4 className="text-xs font-bold uppercase tracking-wider text-muted-foreground">
                        {t("productDetail.exportForms.sizesHeading")}
                      </h4>
                      <div className="mt-2.5 flex flex-wrap gap-2">
                        {format.sizes.map((size) => (
                          <span
                            key={size}
                            className="rounded-lg border border-border/70 bg-surface-alt px-3 py-1.5 text-xs font-bold text-navy shadow-2xs"
                          >
                            {size}
                          </span>
                        ))}
                      </div>
                    </div>
                  )}

                  {format.options && format.options.length > 0 && (
                    <div className="mt-6 grid gap-3 sm:grid-cols-2">
                      {format.options.map((opt, oi) => (
                        <div
                          key={oi}
                          className="space-y-1 rounded-xl border border-border/50 bg-surface-alt/70 p-3.5"
                        >
                          <div className="text-xs font-bold text-navy">
                            {tp(`exportForm.${fi}.option.${oi}.label`, opt.label)}
                          </div>
                          <div className="text-xs leading-relaxed text-muted-foreground">
                            {tp(`exportForm.${fi}.option.${oi}.value`, opt.value)}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}

                  {format.notes && (
                    <p className="mt-5 border-t border-border/60 pt-4 text-xs leading-relaxed text-muted-foreground">
                      {tp(`exportForm.${fi}.notes`, format.notes)}
                    </p>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </Section>

      {/* Logistics & Cold-Chain Packaging. The live-fish viability window is the strongest proof of
          operational knowledge on this page, so it gets its own worked example
          rather than a claim. */}
      <Section band className="border-y border-border/60">
        <div className="mx-auto max-w-5xl">
          <div className="mb-10 max-w-3xl space-y-3">
            <h2 className="font-display text-3xl font-bold text-navy">
              {t("flounderExport.logistics.heading")}
            </h2>
            <p className="text-sm leading-relaxed text-muted-foreground">
              {t("flounderExport.logistics.description")}
            </p>
          </div>

          <div className="grid gap-6 md:grid-cols-3">
            <div className="rounded-2xl border border-border/80 bg-background p-6 shadow-sm">
              <h3 className="flex items-center gap-2 font-display text-base font-bold text-navy">
                <Plane className="h-4 w-4 text-mint-ink" aria-hidden="true" />
                {t("flounderExport.logistics.air.title")}
              </h3>
              <p className="mt-2 text-xs leading-relaxed text-muted-foreground">
                {t("flounderExport.logistics.air.body")}
              </p>
            </div>
            <div className="rounded-2xl border border-border/80 bg-background p-6 shadow-sm">
              <h3 className="flex items-center gap-2 font-display text-base font-bold text-navy">
                <Ship className="h-4 w-4 text-mint-ink" aria-hidden="true" />
                {t("flounderExport.logistics.sea.title")}
              </h3>
              <p className="mt-2 text-xs leading-relaxed text-muted-foreground">
                {t("flounderExport.logistics.sea.body")}
              </p>
            </div>
            <div className="rounded-2xl border border-border/80 bg-background p-6 shadow-sm">
              <h3 className="flex items-center gap-2 font-display text-base font-bold text-navy">
                <ThermometerSnowflake className="h-4 w-4 text-mint-ink" aria-hidden="true" />
                Thermal Packaging
              </h3>
              <p className="mt-2 text-xs leading-relaxed text-muted-foreground">
                6 kg and 10 kg EPS insulated master cartons with specialized ice-pack barriers to
                maintain 0–2 °C core temperatures throughout flight connections.
              </p>
            </div>
          </div>

          {/* Visual packaging spotlight */}
          <div className="mt-6 grid gap-6 rounded-2xl border border-border/80 bg-background p-6 shadow-sm md:grid-cols-12 md:items-center md:p-8">
            <div className="md:col-span-5">
              <div className="overflow-hidden rounded-xl border border-border/80 bg-surface-alt shadow-inner">
                <img
                  src={vacuumEpsImg}
                  alt="Authentic VARS export olive flounder packed in EPS container with vacuum seals"
                  className="h-56 w-full object-cover"
                  loading="lazy"
                />
              </div>
            </div>
            <div className="space-y-3 md:col-span-7">
              <div className="inline-flex items-center gap-2 text-xs font-bold uppercase tracking-wider text-mint-ink">
                <Package className="h-4 w-4" /> Cold-Chain Integrity
              </div>
              <h4 className="font-display text-xl font-bold text-navy">
                Expanded Polystyrene (EPS) Protective Shippers
              </h4>
              <p className="text-xs leading-relaxed text-muted-foreground">
                Our packaging prevents thermal spikes, handling compression, and leakage during
                multi-leg air freight. Temperature recorders accompany commercial export orders to
                confirm unbroken cold chains upon customs release.
              </p>
              <div className="flex flex-wrap gap-2 pt-2">
                <span className="rounded-lg border border-border/60 bg-surface-alt/70 px-2.5 py-1 text-xs font-bold text-navy">
                  6 kg / 10 kg Units
                </span>
                <span className="rounded-lg border border-border/60 bg-surface-alt/70 px-2.5 py-1 text-xs font-bold text-navy">
                  Continuous Data Logging
                </span>
                <span className="rounded-lg border border-border/60 bg-surface-alt/70 px-2.5 py-1 text-xs font-bold text-navy">
                  IATA Compliant
                </span>
              </div>
            </div>
          </div>

          {/* Worked corridor, stage by stage. */}
          <div className="mt-6 rounded-2xl border border-primary/25 bg-background p-6 shadow-sm md:p-8">
            <h3 className="flex items-center gap-2 font-display text-base font-bold text-navy">
              <Clock className="h-4 w-4 text-mint-ink" aria-hidden="true" />
              {t("flounderExport.corridor.title")}
            </h3>
            <p className="mt-2 text-xs leading-relaxed text-muted-foreground">
              {t("flounderExport.corridor.intro")}
            </p>
            <ol className="mt-5 grid gap-3 sm:grid-cols-3">
              {["a", "b", "c"].map((leg, i) => (
                <li key={leg} className="rounded-xl border border-border/60 bg-surface-alt/60 p-4">
                  <div className="font-display text-lg font-bold text-navy">
                    {t(`flounderExport.corridor.leg${i + 1}.time`)}
                  </div>
                  <div className="mt-1 text-xs leading-relaxed text-muted-foreground">
                    {t(`flounderExport.corridor.leg${i + 1}.label`)}
                  </div>
                </li>
              ))}
            </ol>
            <p className="mt-5 border-t border-border/60 pt-4 text-xs font-semibold text-navy">
              {t("flounderExport.corridor.total")}
            </p>
          </div>
        </div>
      </Section>

      {/* Documentation */}
      <Section>
        <div className="mx-auto max-w-5xl rounded-2xl border border-border/80 bg-background p-6 shadow-sm md:p-8">
          <h2 className="font-display text-2xl font-bold text-navy">
            {t("productDetail.exportForms.documentsHeading")}
          </h2>
          <p className="mt-2 max-w-3xl text-sm leading-relaxed text-muted-foreground">
            {t("productDetail.exportForms.documentsLead")}
          </p>
          <div className="mt-5 flex flex-wrap gap-2">
            {/* Certification names are proper nouns — never translated. */}
            {documents.map((doc) => (
              <span
                key={doc}
                className="inline-flex items-center gap-1.5 rounded-lg border border-mint/30 bg-mint/15 px-2.5 py-1 text-xs font-bold text-navy"
              >
                <ShieldCheck className="h-3 w-3 text-primary" aria-hidden="true" />
                {doc}
              </span>
            ))}
          </div>
        </div>
      </Section>

      {/* What we need in order to quote. Stating it up front shortens the
          back-and-forth: the drawer cannot capture these as structured fields
          yet, so the buyer is told what to include in their message. */}
      <Section band className="border-t border-border/60">
        <div className="mx-auto max-w-4xl">
          <div className="mb-8 space-y-3">
            <h2 className="flex items-center gap-2 font-display text-2xl font-bold text-navy">
              <ClipboardList className="h-5 w-5 text-mint-ink" aria-hidden="true" />
              {t("flounderExport.rfq.heading")}
            </h2>
            <p className="text-sm leading-relaxed text-muted-foreground">
              {t("flounderExport.rfq.description")}
            </p>
          </div>
          <ul className="grid gap-3 sm:grid-cols-2">
            {[1, 2, 3, 4, 5, 6].map((n) => (
              <li
                key={n}
                className="flex items-start gap-2 rounded-xl border border-border/60 bg-background p-3"
              >
                <CheckCircle2
                  className="mt-0.5 h-4 w-4 shrink-0 text-mint-ink"
                  aria-hidden="true"
                />
                <span className="text-xs leading-relaxed text-navy/80">
                  {t(`flounderExport.rfq.item${n}`)}
                </span>
              </li>
            ))}
          </ul>

          <div className="mt-8 text-center">
            <Button
              size="lg"
              onClick={() => setQuoteOpen(true)}
              className="rounded-xl font-bold shadow-lg shadow-primary/20"
            >
              {t("flounderExport.rfq.cta")} <ArrowRight className="ms-2 h-4 w-4" />
            </Button>
          </div>
        </div>
      </Section>

      <QuoteDrawer
        open={quoteOpen}
        onOpenChange={setQuoteOpen}
        productSlug="olive-flounder"
        productTitle={tp("title", product?.title ?? "Olive Flounder")}
        source={SUBMISSION_SOURCES.oliveFlounderLanding}
      />

      <FaqSection page="oliveFlounder" />
    </>
  );
}
